import { useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Link } from 'react-router';
import { compareBy, useUrlNumber, useUrlSort, useUrlState, type UrlSort } from '../lib/useUrlState';
import { EmptyState } from '../components/EmptyState';
import { Pager, usePaged } from '../components/Pager';
import { SortHeader } from '../components/SortHeader';
import { formatDateTime, formatDuration } from '../lib/format';
import { RefreshButton } from '../components/RefreshButton';

const ALERTS = gql`
  query Alerts {
    alertsMonitored
    estateAlerts {
      alertname
      sev
      activeAt
      serviceName
      repoGitlabProjectId
      labelsJson
      grafanaExploreUrl
      grafanaAlertUrl
    }
    orphanAlerts {
      alertname
      sev
      activeAt
      labelsJson
      grafanaExploreUrl
      grafanaAlertUrl
    }
  }
`;

interface FiringRow {
  alertname: string;
  sev?: string;
  activeAt: string;
  serviceName?: string;
  repoGitlabProjectId?: number;
  labelsJson: string;
  grafanaExploreUrl: string;
  grafanaAlertUrl: string;
}
interface AlertsData {
  alertsMonitored: boolean;
  estateAlerts: FiringRow[];
  orphanAlerts: FiringRow[];
}

/** FiringRow + the cluster/namespace parsed ONCE, so sorting doesn't re-parse
 *  labelsJson per comparison. `cluster`/`namespace` stay separate for the cell
 *  (the cluster half links to its detail page); `clusterNs` is the sort key. */
interface EnrichedRow extends FiringRow {
  cluster?: string;
  namespace?: string;
  clusterNs: string;
}

/** Sev chip — reuses the house status badge colors (no new CSS): sev0/sev1
 *  read as failure (red), sev2 as warning (amber), everything else muted. */
function SevBadge({ sev }: { sev?: string }) {
  if (!sev) return <span className="badge quiet">—</span>;
  const cls = sev === 'sev0' || sev === 'sev1' ? 'failed' : sev === 'sev2' ? 'inferred' : 'quiet';
  return <span className={`badge ${cls}`}>{sev}</span>;
}

/** `labelsJson` is an opaque string on the wire — cluster/namespace are just
 *  whichever labels Alertmanager happened to carry, so this stays defensive. */
function parseLabels(json: string): Record<string, string> {
  try {
    const v = JSON.parse(json);
    return v && typeof v === 'object' ? v : {};
  } catch {
    return {};
  }
}

function enrich(a: FiringRow): EnrichedRow {
  const labels = parseLabels(a.labelsJson);
  return {
    ...a,
    cluster: labels.cluster,
    namespace: labels.namespace,
    clusterNs: [labels.cluster, labels.namespace].filter(Boolean).join(' / '),
  };
}

function Since({ activeAt }: { activeAt: string }) {
  const dur = formatDuration(activeAt, new Date().toISOString());
  return <span title={formatDateTime(activeAt)}>{dur === '—' ? '—' : `${dur} ago`}</span>;
}

type Tab = 'firing' | 'orphans';

/** Sort accessor per column key. Default 'sev' ascending = sev0 (worst) first;
 *  compareBy puts null/undefined last in either direction, so unlabeled rows
 *  never float to the top. */
function pickFor(key: string): (r: EnrichedRow) => unknown {
  switch (key) {
    case 'alertname':
      return (r) => r.alertname;
    case 'service':
      return (r) => r.serviceName;
    case 'cluster':
      return (r) => r.clusterNs;
    case 'since':
      return (r) => r.activeAt;
    default:
      return (r) => r.sev;
  }
}

export function AlertsPage() {
  const [tabRaw, setTab] = useUrlState('tab', 'firing');
  const tab: Tab = tabRaw === 'orphans' ? 'orphans' : 'firing';
  // URL state for the filter too (shareable, house philosophy) — and NO manual
  // page reset anywhere: useUrlState deletes `page` on any other key's change
  // already. Composing an explicit setPage(1) alongside another setter is the
  // documented two-setters-clobber bug (see useUrlSort's comment) — it silently
  // discarded the tab write and froze the tab switcher.
  const [sevFilter, setSevFilter] = useUrlState('sev', '');
  const sort = useUrlSort('sev', 'asc');
  const [page, setPage] = useUrlNumber('page', 1);

  const { data, loading, error, refetch } = useQuery<AlertsData>(ALERTS, { fetchPolicy: 'cache-and-network' });

  const firing = data?.estateAlerts ?? [];
  const orphans = data?.orphanAlerts ?? [];
  const sevs = useMemo(() => [...new Set(firing.map((a) => a.sev).filter((s): s is string => !!s))].sort(), [firing]);
  const rows = tab === 'orphans' ? orphans : firing;
  const sorted = useMemo(() => {
    const filtered = sevFilter ? rows.filter((a) => a.sev === sevFilter) : rows;
    const enriched = filtered.map(enrich);
    return compareBy(enriched, pickFor(sort.key), sort.dir);
  }, [rows, sevFilter, sort.key, sort.dir]);
  const paged = usePaged(sorted, 25, { page, onPage: setPage });

  if (loading && !data)
    return (
      <div className="page">
        <div className="skeleton-row-group">
          {[0, 1, 2, 3, 4].map((i) => (
            <div className="skeleton-row" key={i} />
          ))}
        </div>
      </div>
    );
  if (error)
    return (
      <div className="page">
        <p className="error">Couldn&apos;t load alerts — the API didn&apos;t respond.</p>
        <p className="muted">If this persists, ask in #devops-infra.</p>
      </div>
    );

  return (
    <div className="page">
      <div className="toolbar">
        <div className="view-toggle">
          <button className={tab === 'firing' ? 'active' : ''} onClick={() => setTab('firing')}>
            Firing
          </button>
          <button className={tab === 'orphans' ? 'active' : ''} onClick={() => setTab('orphans')}>
            Orphans
          </button>
        </div>
        <div className="filter-row" style={{ flex: 1 }}>
          <RefreshButton refetch={() => refetch().catch(() => undefined)} />
          <select value={sevFilter} onChange={(e) => setSevFilter(e.target.value)} title="filter by severity">
            <option value="">All severities</option>
            {sevs.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!data?.alertsMonitored ? (
        <EmptyState
          icon="alert"
          title="Monitoring not configured"
          body="This environment has no Alertmanager wired up yet, so there is nothing to show here."
        />
      ) : (
        <>
          {tab === 'orphans' && (
            <p className="unowned-note">
              Firing alerts matching no service — declare a selector on the repo page.
            </p>
          )}
          <FiringTable rows={paged.pageItems} showService={tab === 'firing'} sort={sort} />
          <Pager
            page={paged.page}
            pageCount={paged.pageCount}
            total={paged.total}
            start={paged.start}
            pageSize={paged.pageSize}
            onPage={paged.setPage}
          />
        </>
      )}
    </div>
  );
}

function FiringTable({ rows, showService, sort }: { rows: EnrichedRow[]; showService: boolean; sort: UrlSort }) {
  if (!rows.length) return <p className="muted">No alerts here.</p>;
  return (
    <table className="data-list--primary">
      <thead>
        <tr>
          <SortHeader label="Sev" sortKey="sev" sort={sort} />
          <SortHeader label="Alert" sortKey="alertname" sort={sort} />
          {showService && <SortHeader label="Service" sortKey="service" sort={sort} />}
          <SortHeader label="Cluster / namespace" sortKey="cluster" sort={sort} />
          <SortHeader label="Since" sortKey="since" sort={sort} />
          <th>Grafana</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((a, i) => (
          <tr key={`${a.alertname}-${i}`}>
            <td>
              <SevBadge sev={a.sev} />
            </td>
            <td className="table-title">{a.alertname}</td>
            {showService && (
              <td>
                {a.serviceName ? (
                  a.repoGitlabProjectId != null ? (
                    <Link to={`/repos/${a.repoGitlabProjectId}`} title="open the repo page">
                      {a.serviceName}
                    </Link>
                  ) : (
                    a.serviceName
                  )
                ) : (
                  '—'
                )}
              </td>
            )}
            <td>
              {a.cluster ? (
                <>
                  <Link to={`/clusters/${encodeURIComponent(a.cluster)}`} title="open the cluster page">
                    {a.cluster}
                  </Link>
                  {a.namespace ? <span className="muted"> / {a.namespace}</span> : null}
                </>
              ) : (
                a.clusterNs || '—'
              )}
            </td>
            <td>
              <Since activeAt={a.activeAt} />
            </td>
            <td>
              <a href={a.grafanaAlertUrl} target="_blank" rel="noreferrer" title="the alert rule in Grafana (definition, state, history)">
                Alert
              </a>
              <span className="muted"> · </span>
              <a href={a.grafanaExploreUrl} target="_blank" rel="noreferrer" title="logs for this cluster/namespace">
                Logs
              </a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
